import React, { useState } from "react";
import { ChevronDown, User, Briefcase, TrendingUp, Home, Users, Compass, Building2, Globe2, Split, Layers, ChevronRight, Sparkles, Info } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { LIFE_CHAPTERS } from "@/lib/infiniteSelves/lifeChapters";
import type { LifeRecord } from "@/lib/infiniteSelves";
import { scrollToElement, HEADER_OFFSET } from "@/lib/scroll";
export interface ChaptersTabProps {
  life: LifeRecord | null;
  cardHue?: number;
}
export default function ChaptersTab({
  life,
  cardHue = 186
}: ChaptersTabProps): JSX.Element {
  const [openChapters, setOpenChapters] = useState<Record<string, boolean>>({
    identity: true
  });
  const toggleChapter = (id: string) => {
    setOpenChapters(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const expandAll = () => {
    const allOpen: Record<string, boolean> = {};
    for (const ch of LIFE_CHAPTERS) {
      allOpen[ch.id] = true;
    }
    setOpenChapters(allOpen);
  };
  const collapseAll = () => {
    setOpenChapters({});
  };
  // If life record is missing, show catalog with neutral notice
  if (!life) {
    return <div className="space-y-6">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border text-xs text-muted-foreground font-sans">
          <Info className="size-4 text-primary shrink-0" />
          <span>This chapter is not available for this result. Generate again to see it.</span>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between border-b border-border/60 pb-2">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
              <Layers className="size-3.5 text-primary shrink-0" />
              <span>Full Dossier Chapter Catalog ({LIFE_CHAPTERS.length} Chapters)</span>
            </span>
            <span className="text-[10px] font-mono text-primary uppercase">
              Multiverse Life Dossier
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {LIFE_CHAPTERS.map((ch, idx) => <div key={ch.id} className="p-3.5 rounded-xl bg-card/65 border border-border/70 space-y-1.5 relative overflow-hidden">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-serif font-bold text-foreground flex items-center gap-2">
                    <span className="text-[10px] font-mono text-primary font-semibold">
                      Ch. {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span>{ch.title}</span>
                  </span>
                  <Badge variant="outline" className="text-[9px] font-mono uppercase text-muted-foreground border-border/60">
                    Chapter {idx + 1}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed font-sans">
                  {ch.blurb}
                </p>
              </div>)}
          </div>
        </div>
      </div>;
  }

  // Interactive accordion / chapters reader
  const scrollToChapter = (id: string) => {
    setOpenChapters(prev => ({
      ...prev,
      [id]: true
    }));
    scrollToElement(`#chapter-${id}`, {
      block: "start",
      offset: HEADER_OFFSET + 12,
      onlyIfNeeded: true
    });
  };
  return <div className="space-y-6">
      {/* Chapter Pills Quick Index */}
      <div className="p-3.5 rounded-xl bg-card/85 border border-border/80 space-y-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
            <Compass className="size-3.5 text-primary shrink-0" />
            <span>Chapter Navigator</span>
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={expandAll} className="text-[11px] font-mono text-primary hover:underline cursor-pointer min-h-[36px] px-2">
              Expand all
            </button>
            <span className="text-muted-foreground">·</span>
            <button type="button" onClick={collapseAll} className="text-[11px] font-mono text-muted-foreground hover:text-foreground cursor-pointer min-h-[36px] px-2">
              Collapse all
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 pt-1">
          {LIFE_CHAPTERS.map((ch, idx) => {
          const isOpen = Boolean(openChapters[ch.id]);
          return <button key={ch.id} type="button" onClick={() => scrollToChapter(ch.id)} className={`min-h-[36px] px-2.5 py-1 rounded-full text-[11px] font-mono transition-all duration-150 border cursor-pointer flex items-center gap-1.5 ${isOpen ? "bg-primary/15 border-primary/60 text-primary font-semibold shadow-[0_0_8px_var(--glow-primary)]" : "bg-muted/40 border-border/60 text-muted-foreground hover:text-foreground hover:bg-muted/70"}`}>
                <span>{idx + 1}.</span>
                <span>{ch.title}</span>
              </button>;
        })}
        </div>
      </div>

      {/* Chapter 1: Identity & Biography */}
      <section id="chapter-identity" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("identity")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.identity)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-primary/10 border border-primary/30 text-primary shrink-0">
              <User className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                Chapter 01
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                Identity & Biography
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.identity ? "rotate-180" : ""}`} />
        </button>

        {openChapters.identity && <div className="p-4 sm:p-6 border-t border-border/60 space-y-4 font-sans text-xs sm:text-sm">
            <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border/60">
              <span className="font-mono text-xs text-primary font-bold">
                Chronological Age: {life.identity.age} years
              </span>
              {life.identity.ageNote && <>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-xs text-muted-foreground italic">
                    {life.identity.ageNote}
                  </span>
                </>}
            </div>

            {life.identity.personalitySummary && <p className="text-foreground/95 italic border-l-2 border-primary/60 pl-3 leading-relaxed">
                "{life.identity.personalitySummary}"
              </p>}

            <div className="space-y-3 pt-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-bold block">
                Biographical Narrative
              </span>
              {life.identity.biography?.map((para, idx) => <p key={idx} className="text-foreground/90 leading-relaxed">
                  {para}
                </p>)}
            </div>
          </div>}
      </section>

      {/* Chapter 2: Work & Expertise */}
      <section id="chapter-work" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("work")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.work)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-chart-3/15 border border-chart-3/40 text-chart-3 shrink-0">
              <Briefcase className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-chart-3 font-bold block">
                Chapter 02
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                Work & Expertise
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.work ? "rotate-180" : ""}`} />
        </button>

        {openChapters.work && <div className="p-4 sm:p-6 border-t border-border/60 space-y-5 font-sans text-xs sm:text-sm">
            <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
              <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                Occupation & Physical Workplace
              </span>
              <p className="font-serif font-bold text-base text-foreground">
                {life.work.occupation}
              </p>
              {life.work.workplace && <p className="text-xs text-muted-foreground leading-relaxed">
                  Base of operations: {life.work.workplace}
                </p>}
            </div>

            {/* Expertise Chips */}
            {life.work.expertise?.length > 0 && <div className="space-y-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-bold block">
                  Core Proficiencies & Technical Domains
                </span>
                <div className="flex flex-wrap gap-2">
                  {life.work.expertise.map((exp, idx) => <span key={idx} className="ql-chip text-xs border-primary/30 bg-muted/50 text-foreground/90">
                      {exp}
                    </span>)}
                </div>
              </div>}

            {/* Career History Ladder */}
            {life.work.careerHistory?.length > 0 && <div className="space-y-2 pt-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-bold block">
                  Career Progression Ladder
                </span>
                <div className="space-y-3 relative before:absolute before:top-2 before:bottom-2 before:left-3.5 before:w-0.5 before:bg-border/70 pl-7">
                  {life.work.careerHistory.map((stage, idx) => <div key={idx} className="relative space-y-1">
                      <span className="absolute -left-7 top-1 size-3 rounded-full border-2 border-background" style={{
                backgroundColor: `hsl(${cardHue} 100% 55%)`
              }} aria-hidden="true" />
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-serif font-bold text-foreground text-xs sm:text-sm">
                          {stage.stage}
                        </span>
                        <span className="font-mono text-[10px] text-primary">
                          {stage.years}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {stage.text}
                      </p>
                    </div>)}
                </div>
              </div>}

            {/* Responsibilities */}
            {life.work.responsibilities?.length > 0 && <div className="space-y-2 pt-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-bold block">
                  Operational Responsibilities
                </span>
                <ul className="space-y-1.5 text-xs text-foreground/90">
                  {life.work.responsibilities.map((resp, idx) => <li key={idx} className="flex items-start gap-2">
                      <ChevronRight className="size-3 text-primary mt-1 shrink-0" />
                      <span className="leading-relaxed">{resp}</span>
                    </li>)}
                </ul>
              </div>}
          </div>}
      </section>

      {/* Chapter 3: Standing & Influence */}
      <section id="chapter-standing" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("standing")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.standing)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-primary/10 border border-primary/30 text-primary shrink-0">
              <TrendingUp className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                Chapter 03
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                Standing & Influence
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.standing ? "rotate-180" : ""}`} />
        </button>

        {openChapters.standing && <div className="p-4 sm:p-6 border-t border-border/60 space-y-4 font-sans text-xs sm:text-sm">
            {/* Influence Meter */}
            <div className="p-4 rounded-xl bg-card border border-border/80 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="font-mono uppercase font-bold text-primary">
                  Systemic Influence Quotient
                </span>
                <span className="font-mono text-primary font-bold text-sm">
                  {life.standing.influenceLevel} / 100
                </span>
              </div>
              <Progress value={life.standing.influenceLevel} className="h-2 bg-muted/80" />
              <p className="text-xs text-muted-foreground pt-1 leading-relaxed">
                {life.standing.influence}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Wealth Bracket & Resources
                </span>
                <span className="font-serif font-bold text-foreground block">
                  {life.standing.wealthBracket}
                </span>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {life.standing.wealthText}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Social Class & Caste
                </span>
                <span className="font-serif font-bold text-foreground block">
                  {life.standing.socialClass}
                </span>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {life.standing.economicCircumstances}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {life.standing.reputation && <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-1">
                  <span className="text-[10px] font-mono uppercase text-primary font-semibold block">
                    Public Reputation
                  </span>
                  <p className="text-xs text-foreground/90 leading-relaxed">
                    {life.standing.reputation}
                  </p>
                </div>}
              {life.standing.prestige && <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-1">
                  <span className="text-[10px] font-mono uppercase text-primary font-semibold block">
                    Institutional Prestige
                  </span>
                  <p className="text-xs text-foreground/90 leading-relaxed">
                    {life.standing.prestige}
                  </p>
                </div>}
            </div>
          </div>}
      </section>

      {/* Chapter 4: Home & Daily Life */}
      <section id="chapter-home" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("home")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.home)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-primary/10 border border-primary/30 text-primary shrink-0">
              <Home className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                Chapter 04
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                Home & Daily Life
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.home ? "rotate-180" : ""}`} />
        </button>

        {openChapters.home && <div className="p-4 sm:p-6 border-t border-border/60 space-y-4 font-sans text-xs sm:text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Dwelling Quarters
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.home.dwelling}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Immediate Environment
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.home.environment}
                </p>
              </div>
            </div>

            <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-1">
              <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                Domestic Lifestyle & Transit
              </span>
              <p className="text-xs text-foreground/90 leading-relaxed">
                {life.home.lifestyle}
              </p>
              {life.home.transport && <p className="text-xs text-primary pt-1">
                  Transit mechanism: {life.home.transport}
                </p>}
            </div>

            {/* Possessions Chips */}
            {life.home.possessions?.length > 0 && <div className="space-y-2 pt-1">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-bold block">
                  Key Personal Possessions
                </span>
                <div className="flex flex-wrap gap-2">
                  {life.home.possessions.map((item, idx) => <span key={idx} className="ql-chip text-xs border-primary/20 bg-muted/60 text-foreground/90">
                      {item}
                    </span>)}
                </div>
              </div>}

            {/* Daily Routine Sequence */}
            {life.home.routine?.length > 0 && <div className="space-y-2 pt-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-bold block">
                  Daily Domestic Cadence
                </span>
                <div className="space-y-2">
                  {life.home.routine.map((entry, idx) => {
              const parts = entry.split(" — ");
              const time = parts.length > 1 ? parts[0] : null;
              const text = parts.length > 1 ? parts.slice(1).join(" — ") : entry;
              return <div key={idx} className="p-2.5 rounded-lg bg-muted/30 border border-border/40 flex items-start gap-2.5 text-xs">
                        {time ? <span className="font-mono text-[11px] text-primary font-bold shrink-0 min-w-[70px]">
                            {time}
                          </span> : <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />}
                        <span className="text-foreground/90 leading-relaxed font-sans">
                          {text}
                        </span>
                      </div>;
            })}
                </div>
              </div>}
          </div>}
      </section>

      {/* Chapter 5: People & Connections */}
      <section id="chapter-people" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("people")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.people)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-primary/10 border border-primary/30 text-primary shrink-0">
              <Users className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                Chapter 05
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                People & Connections
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.people ? "rotate-180" : ""}`} />
        </button>

        {openChapters.people && <div className="p-4 sm:p-6 border-t border-border/60 space-y-3 font-sans text-xs sm:text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Family Lineage & Origins
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.people.family}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Key Partnerships & Bonds
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.people.relationships}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Close Friendships & Confidantes
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.people.friendships}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Wider Social Network & Cohorts
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.people.socialCircle}
                </p>
              </div>
            </div>
          </div>}
      </section>

      {/* Chapter 6: Formative Years */}
      <section id="chapter-formative" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("formative")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.formative)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-primary/10 border border-primary/30 text-primary shrink-0">
              <Sparkles className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                Chapter 06
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                Formative Years
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.formative ? "rotate-180" : ""}`} />
        </button>

        {openChapters.formative && <div className="p-4 sm:p-6 border-t border-border/60 space-y-4 font-sans text-xs sm:text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Early Childhood Context
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.formative.childhood}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Foundational Education & Mentorship
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.formative.education}
                </p>
              </div>
            </div>

            {/* Decisions, Achievements, Setbacks, Turning Points */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
              {life.formative.decisions?.length > 0 && <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-2">
                  <span className="text-[10px] font-mono uppercase text-primary font-bold block">
                    Formative Decisions
                  </span>
                  <ul className="space-y-1.5 text-xs text-foreground/90">
                    {life.formative.decisions.map((dec, idx) => <li key={idx} className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                        <span className="leading-relaxed">{dec}</span>
                      </li>)}
                  </ul>
                </div>}

              {life.formative.achievements?.length > 0 && <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-2">
                  <span className="text-[10px] font-mono uppercase text-primary font-bold block">
                    Early Achievements
                  </span>
                  <ul className="space-y-1.5 text-xs text-foreground/90">
                    {life.formative.achievements.map((ach, idx) => <li key={idx} className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" />
                        <span className="leading-relaxed">{ach}</span>
                      </li>)}
                  </ul>
                </div>}

              {life.formative.setbacks?.length > 0 && <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-2">
                  <span className="text-[10px] font-mono uppercase text-destructive font-bold block">
                    Defining Setbacks & Scars
                  </span>
                  <ul className="space-y-1.5 text-xs text-foreground/90">
                    {life.formative.setbacks.map((setback, idx) => <li key={idx} className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-destructive mt-1.5 shrink-0" />
                        <span className="leading-relaxed">{setback}</span>
                      </li>)}
                  </ul>
                </div>}

              {life.formative.turningPoints?.length > 0 && <div className="p-3.5 rounded-lg bg-muted/30 border border-border/60 space-y-2">
                  <span className="text-[10px] font-mono uppercase text-chart-3 font-bold block">
                    Threshold Turning Points
                  </span>
                  <ul className="space-y-1.5 text-xs text-foreground/90">
                    {life.formative.turningPoints.map((tp, idx) => <li key={idx} className="flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-chart-3 mt-1.5 shrink-0" />
                        <span className="leading-relaxed">{tp}</span>
                      </li>)}
                  </ul>
                </div>}
            </div>
          </div>}
      </section>

      {/* Chapter 7: Society & Institutions */}
      <section id="chapter-society" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("society")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.society)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-primary/10 border border-primary/30 text-primary shrink-0">
              <Building2 className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                Chapter 07
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                Society & Institutions
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.society ? "rotate-180" : ""}`} />
        </button>

        {openChapters.society && <div className="p-4 sm:p-6 border-t border-border/60 space-y-4 font-sans text-xs sm:text-sm">
            <p className="text-foreground/90 leading-relaxed font-sans">
              {life.society.summary}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Legal Architecture & Governance
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.society.laws}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Cultural Norms & Taboos
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.society.culture}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Governing Bodies & Institutions
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.society.institutions}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Civic Expectations & Social Mobility
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.society.expectations}
                </p>
                {life.society.opportunity && <p className="text-xs text-primary pt-1">
                    Opportunity paradigm: {life.society.opportunity}
                  </p>}
              </div>
            </div>
          </div>}
      </section>

      {/* Chapter 8: World & Technology */}
      <section id="chapter-world" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("world")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.world)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-primary/10 border border-primary/30 text-primary shrink-0">
              <Globe2 className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-primary font-bold block">
                Chapter 08
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                World & Technology
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.world ? "rotate-180" : ""}`} />
        </button>

        {openChapters.world && <div className="p-4 sm:p-6 border-t border-border/60 space-y-3 font-sans text-xs sm:text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Technological Paradigm
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.world.technology}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Scientific Foundations
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.world.science}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Geography & Habitable Zones
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.world.geography}
                </p>
              </div>

              <div className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1">
                <span className="text-[10px] font-mono uppercase text-muted-foreground font-semibold block">
                  Deep Planetary History & Environment
                </span>
                <p className="text-xs text-foreground/90 leading-relaxed">
                  {life.world.history}
                </p>
                {life.world.environment && <p className="text-xs text-muted-foreground pt-1">
                    Ecosystem state: {life.world.environment}
                  </p>}
              </div>
            </div>
          </div>}
      </section>

      {/* Chapter 9: How This World Differs from Earth */}
      <section id="chapter-differs" className="rounded-xl border border-border bg-card/80 overflow-hidden shadow-sm">
        <button type="button" onClick={() => toggleChapter("differs")} className="w-full p-4 sm:p-5 flex items-center justify-between gap-3 text-left hover:bg-muted/40 transition-colors cursor-pointer min-h-[44px]" aria-expanded={Boolean(openChapters.differs)}>
          <div className="flex items-center gap-3 min-w-0">
            <span className="p-2 rounded-lg bg-chart-3/15 border border-chart-3/40 text-chart-3 shrink-0">
              <Split className="size-4" />
            </span>
            <div className="min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-chart-3 font-bold block">
                Chapter 09
              </span>
              <h3 className="font-serif text-base sm:text-lg font-bold text-foreground truncate">
                How This World Differs from Earth
              </h3>
            </div>
          </div>
          <ChevronDown className={`size-4 text-muted-foreground shrink-0 transition-transform duration-200 ${openChapters.differs ? "rotate-180" : ""}`} />
        </button>

        {openChapters.differs && <div className="p-4 sm:p-6 border-t border-border/60 space-y-5 font-sans text-xs sm:text-sm">
            {life.howDiffers.headline && <h4 className="font-serif font-bold text-base sm:text-lg text-primary">
                {life.howDiffers.headline}
              </h4>}

            {life.howDiffers.paragraphs?.length > 0 && <div className="space-y-3">
                {life.howDiffers.paragraphs.map((p, idx) => <p key={idx} className="text-foreground/90 leading-relaxed">
                    {p}
                  </p>)}
              </div>}

            {life.howDiffers.keyDifferences?.length > 0 && <div className="space-y-3 pt-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-bold block">
                  Key Structural Divergences
                </span>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {life.howDiffers.keyDifferences.map((kd, idx) => <div key={idx} className="p-3.5 rounded-lg bg-muted/40 border border-border/60 space-y-1.5">
                      <span className="font-serif font-bold text-foreground text-xs sm:text-sm block">
                        {kd.title}
                      </span>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {kd.text}
                      </p>
                    </div>)}
                </div>
              </div>}
          </div>}
      </section>
    </div>;
}